import { Controller } from '@hotwired/stimulus'
import 'utils/multiselect' // side-effect: registers prod's global .multiselect driver (open/close/search/select/label)

// data_sets_gallery behaviour. The builder MDX shipped no `## JS`, so this mirrors
// DashboardsGallery's filter model on top of prod's real `.multiselect`:
//   - the self-contained utils/multiselect driver owns open/close/search/select
//     + the toggle label; this controller listens for checkbox `change`
//     (delegated on the root) and reads checked values into per-dimension state
//   - filter predicate: within a filter values OR, across filters AND; a filter
//     with no checked option is inactive ("All"). Selected values are matched
//     case-insensitively against each card's data-tags + title.
//   - case-insensitive search over title + description + tags (debounced 150ms)
//   - "Show all": cap visible cards at visibleCount, reveal the rest on click
// Attached to .data-sets-gallery via data-controller="data-sets-gallery-filter".
export default class extends Controller {
  static targets = ['grid', 'search', 'showMore', 'empty']
  static values = { visibleCount: { type: Number, default: 6 } }

  connect() {
    this.cards = Array.from(this.gridTarget.children)
    this.filters = Array.from(
      this.element.querySelectorAll('.multiselect[data-multiselect-dim]')
    )
    this.showAll = false
    this.query = ''
    this.timer = null

    // Cache each card's haystack (tags + title, lowercased) once.
    this.cards.forEach(card => {
      const tags = (card.dataset.tags || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
      const title =
        card.querySelector('.data-sets-gallery__card-title')?.textContent || ''
      const desc =
        card.querySelector('.data-sets-gallery__card-description')
          ?.textContent || ''
      card._tags = tags
      card._haystack = (tags.join(' ') + ' ' + title).toLowerCase()
      card._search = (title + ' ' + desc + ' ' + tags.join(' ')).toLowerCase()
    })

    // The prod multiselect driver toggles the checkboxes; we react to their change.
    this.onMultiselectChange = e => {
      if (
        e.target.classList &&
        e.target.classList.contains('multiselect__checkbox')
      ) {
        this.showAll = false
        this.render()
      }
    }
    this.element.addEventListener('change', this.onMultiselectChange)
    // "Clear All" unchecks without a change event → also resync on click.
    this.onMultiselectClear = e => {
      if (e.target.closest('.multiselect__clear')) {
        requestAnimationFrame(() => {
          this.showAll = false
          this.render()
        })
      }
    }
    this.element.addEventListener('click', this.onMultiselectClear)

    this.render()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.element.removeEventListener('change', this.onMultiselectChange)
    this.element.removeEventListener('click', this.onMultiselectClear)
  }

  // ----- Filter / search / show-more ---------------------------------------
  // Checked values for one multiselect → the lowercased filter keys
  // (data-filter-value). None checked = inactive ("All").
  activeValues(filter) {
    const checked = Array.from(
      filter.querySelectorAll('.multiselect__checkbox:checked')
    )
    if (checked.length === 0) return null
    return checked.map(c => c.dataset.filterValue)
  }

  matches(card) {
    const filterOk = this.filters.every(f => {
      const values = this.activeValues(f)
      return values === null || values.some(v => card._haystack.includes(v))
    })
    if (!filterOk) return false
    const q = this.query.trim().toLowerCase()
    if (!q) return true
    return card._search.includes(q)
  }

  render() {
    const cap = this.visibleCountValue
    let shown = 0
    this.cards.forEach(card => {
      const ok = this.matches(card)
      const visible = ok && (this.showAll || shown < cap)
      card.hidden = !visible
      if (visible) shown++
    })
    const total = this.cards.filter(c => this.matches(c)).length
    if (this.hasShowMoreTarget)
      this.showMoreTarget.hidden = this.showAll || total <= cap
    // Prod-style empty state: shown when the filters/search match nothing.
    if (this.hasEmptyTarget) this.emptyTarget.hidden = total > 0
  }

  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.query = this.searchTarget.value
      this.showAll = false
      this.render()
    }, 150)
  }

  showMore() {
    this.showAll = true
    this.render()
  }
}
